/**
 * Extracts the actual entity ID from compound grouped row IDs.
 *
 * For grouped rows, IDs can be like:
 * - "editor.appointment"
 * - "parentId>editor.appointment"
 * - "columnId:groupValue"
 * - "parentId>columnId:groupValue"
 *
 * This function extracts just the last part (the actual entity ID) that can be used
 * for operations like flagging, editing, etc.
 *
 * @param rowId - The row ID which might be a compound grouped row ID
 * @returns The extracted entity ID (as string or number)
 */
export function extractEntityIdFromGroupedRowId(
  rowId: string | number
): string | number {
  const idStr = String(rowId);

  // If it's a compound ID with dots, arrows, or colons, extract the last part
  if (idStr.includes('.') || idStr.includes('>') || idStr.includes(':')) {
    // Split by dots, arrows, and colons, then take the last part
    const parts = idStr.split(/[.>:]/);
    const lastPart = parts[parts.length - 1];

    // If the last part is empty, return empty string
    if (lastPart === '') {
      return '';
    }

    // If the last part is a number, return it as number, otherwise as string
    const numericId = Number(lastPart);
    return isNaN(numericId) ? lastPart : numericId;
  }

  // If it's not a compound ID, return as-is
  return rowId;
}
